// Content-related type definitions for Rainfactory platform (Frontend)
// This is a copy of the backend types for frontend use
// Defines platform-specific post types and utility functions for backward compatibility

/**
 * Platform-specific post types for social media content
 * Replaces the boolean is_series field with flexible string-based types
 */
export type PlatformPostType =
  // Twitter/X
  | 'single'      // Single standalone tweet
  | 'thread'      // Connected series of tweets
  // Instagram
  | 'single_image' // Single photo post
  | 'carousel'     // Multi-image/video swipeable post (up to 10 items)
  | 'reel'         // Short-form vertical video (15-90 seconds)
  | 'story'        // 24-hour ephemeral content
  | 'video'        // Long-form video content (up to 60 minutes)
  | 'live'         // Real-time streaming (Instagram Live)
  // Facebook
  | 'photo'        // Single photo post
  | 'link'         // Link share with preview
  | 'text'         // Text-only status update
  | 'event'        // Event post
  // LinkedIn (note: some types overlap with other platforms like 'video', 'text', 'link')
  | 'document'     // PDF/document carousel share
  | 'poll'         // Interactive poll
  | 'newsletter'   // Newsletter post
  | 'image'        // Single image post
  | 'article';     // Long-form article (LinkedIn article editor)

/**
 * Platform-specific post type mappings
 * Used for validation and UI display
 */
export const TWITTER_POST_TYPES: PlatformPostType[] = ['single', 'thread'];

export const INSTAGRAM_POST_TYPES: PlatformPostType[] = [
  'single_image',
  'carousel',
  'reel',
  'story',
  'video',
  'live'
];

export const FACEBOOK_POST_TYPES: PlatformPostType[] = [
  'photo',
  'video',
  'reel',
  'carousel',
  'link',
  'text',
  'story',
  'live',
  'event'
];

export const LINKEDIN_POST_TYPES: PlatformPostType[] = [
  'document',
  'video',
  'text',
  'poll',
  'newsletter',
  'live',
  'image',
  'article',
  'link'
];

/**
 * All valid platform post types (for general validation)
 */
export const ALL_PLATFORM_POST_TYPES: PlatformPostType[] = [
  ...TWITTER_POST_TYPES,
  ...INSTAGRAM_POST_TYPES,
  ...FACEBOOK_POST_TYPES,
  ...LINKEDIN_POST_TYPES
];

/**
 * SINGLE SOURCE OF TRUTH: Platform to post types mapping
 * This centralized mapping is used across both frontend and backend
 * to ensure consistency in which post types are valid for each platform
 */
export const PLATFORM_POST_TYPES_MAP: Record<string, PlatformPostType[]> = {
  'twitter': TWITTER_POST_TYPES,
  'x': TWITTER_POST_TYPES, // Twitter alias
  'instagram': INSTAGRAM_POST_TYPES,
  'facebook': FACEBOOK_POST_TYPES,
  'linkedin': LINKEDIN_POST_TYPES
};

/**
 * Supported media types for content generation
 */
export type MediaType = 'image' | 'video' | 'none';

/**
 * Interface for media generation error tracking
 * Captures errors that occur during media generation, search, or enhancement
 */
export interface MediaGenerationError {
    step: string                    // AI_GENERATE, SEARCH_EXISTING, STOCK_SEARCH
    error: string                   // Error message
    taskId?: string                 // KIE task ID (if applicable)
    timestamp: string               // ISO timestamp
    attempts?: number               // Number of attempts (for timeouts)
    retryable: boolean              // Whether error is retryable
    requirementIndex?: number       // Which media requirement failed
}

/**
 * UI labels and descriptions for each post type
 * Used for frontend display in dropdowns, buttons, and UI components
 */
export const POST_TYPE_LABELS: Record<PlatformPostType, {
  label: string;
  description: string;
  whenToUse: string; // Guidance on when to use this post type
  supportedMediaTypes: MediaType[]; // What media types this post type supports
  status: 'available' | 'coming_soon'; // Availability status
}> = {
  // Twitter/X
  'single': {
    label: 'Single Tweet',
    description: 'One standalone tweet',
    whenToUse: 'Use for quick updates, announcements, or standalone thoughts',
    supportedMediaTypes: ['image', 'video'], // Twitter supports both images and videos
    status: 'available'
  },
  'thread': {
    label: 'Thread',
    description: 'Multiple connected tweets',
    whenToUse: 'Use when you have multiple related points or need more than 280 characters',
    supportedMediaTypes: ['image', 'video'], // Threads support media on each tweet
    status: 'available'
  },
  // Instagram
  'single_image': {
    label: 'Single Post',
    description: 'One image or video',
    whenToUse: 'Use for single impactful images or short videos',
    supportedMediaTypes: ['image', 'video'], // Can be either image or video
    status: 'available'
  },
  'carousel': {
    label: 'Carousel',
    description: 'Multiple images/videos (up to 10)',
    whenToUse: 'Use for step-by-step guides, before/after, or multiple product shots',
    supportedMediaTypes: ['image', 'video'], // Carousels support both images and videos
    status: 'available'
  },
  'reel': {
    label: 'Reel',
    description: 'Short-form vertical video',
    whenToUse: 'Use for trending content, quick tips, or viral-style videos (15-90 seconds)',
    supportedMediaTypes: ['video'], // Reels are VIDEO ONLY
    status: 'coming_soon'
  },
  'story': {
    label: 'Story',
    description: '24-hour ephemeral content',
    whenToUse: 'Use for time-sensitive content, behind-the-scenes, or daily updates',
    supportedMediaTypes: ['image', 'video'], // Stories support both
    status: 'coming_soon'
  },
  'video': {
    label: 'Video',
    description: 'Long-form video (up to 60 min)',
    whenToUse: 'Use for tutorials, interviews, or in-depth content',
    supportedMediaTypes: ['video'], // VIDEO ONLY
    status: 'coming_soon'
  },
  'live': {
    label: 'Live Video',
    description: 'Real-time streaming',
    whenToUse: 'Use for Q&As, product launches, or real-time events',
    supportedMediaTypes: ['video'], // VIDEO ONLY (streaming)
    status: 'coming_soon'
  },
  // Facebook
  'photo': {
    label: 'Photo Post',
    description: 'Single photo',
    whenToUse: 'Use for single impactful images or announcements',
    supportedMediaTypes: ['image'], // IMAGE ONLY
    status: 'available'
  },
  'link': {
    label: 'Link Share',
    description: 'Share a link with preview',
    whenToUse: 'Use to share blog posts, articles, or external content',
    supportedMediaTypes: ['image'], // Link previews with image
    status: 'available'
  },
  'text': {
    label: 'Text Post',
    description: 'Text-only status update',
    whenToUse: 'Use for questions, polls, or text-based engagement',
    supportedMediaTypes: ['none'], // TEXT ONLY - no media
    status: 'available'
  },
  'event': {
    label: 'Event',
    description: 'Event post',
    whenToUse: 'Use to promote upcoming events or gatherings',
    supportedMediaTypes: ['image'], // Events use images
    status: 'coming_soon'
  },
  // LinkedIn
  'document': {
    label: 'Document',
    description: 'PDF/document carousel',
    whenToUse: 'Use for reports, whitepapers, or slide decks',
    supportedMediaTypes: ['none'], // Documents are PDFs, not generated media
    status: 'coming_soon'
  },
  'poll': {
    label: 'Poll',
    description: 'Interactive poll',
    whenToUse: 'Use to gather opinions or engage your network',
    supportedMediaTypes: ['none'], // Polls are text-based
    status: 'coming_soon'
  },
  'newsletter': {
    label: 'Newsletter',
    description: 'Newsletter post',
    whenToUse: 'Use for regular long-form content to subscribers',
    supportedMediaTypes: ['image'], // Newsletters can include images
    status: 'coming_soon'
  },
  'image': {
    label: 'Image Post',
    description: 'Single image post',
    whenToUse: 'Use for infographics, quotes, or visual content',
    supportedMediaTypes: ['image'], // IMAGE ONLY
    status: 'available'
  },
  'article': {
    label: 'Article',
    description: 'Long-form article',
    whenToUse: 'Use for thought leadership or in-depth professional content',
    supportedMediaTypes: ['image'], // Articles can include images
    status: 'coming_soon'
  }
};

/**
 * Get the appropriate post type from content_json with fallback to legacy is_series
 * Ensures backward compatibility with existing content
 *
 * @param content_json - The content JSON object
 * @returns The platform post type string
 */
export function getPlatformPostType(content_json: any): PlatformPostType {
  // New format takes precedence
  if (content_json.platform_post_type) {
    return content_json.platform_post_type as PlatformPostType;
  }

  // Fallback to deriving from is_series (legacy)
  if (content_json.is_series !== undefined) {
    const platform = content_json.platform || 'twitter';
    return derivePostTypeFromIsSeries(content_json.is_series, platform);
  }

  // Default to single post
  const platform = content_json.platform || 'twitter';
  return getDefaultSinglePostType(platform);
}

/**
 * Derive platform_post_type from legacy is_series boolean
 * Used for backward compatibility
 *
 * @param is_series - Legacy boolean indicating series/thread
 * @param platform - The social media platform
 * @returns Appropriate platform post type
 */
export function derivePostTypeFromIsSeries(
  is_series: boolean,
  platform: string
): PlatformPostType {
  if (!is_series) {
    return getDefaultSinglePostType(platform);
  }

  // Map legacy true value to appropriate multi-post type per platform
  switch (platform.toLowerCase()) {
    case 'twitter':
      return 'thread';
    case 'instagram':
      return 'carousel';
    case 'facebook':
      return 'carousel';
    case 'linkedin':
      return 'document'; // LinkedIn carousels are document PDFs
    default:
      return 'thread';
  }
}

/**
 * Get the default single post type for a platform
 *
 * @param platform - The social media platform
 * @returns Default single post type for the platform
 */
export function getDefaultSinglePostType(platform: string): PlatformPostType {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return 'single';
    case 'instagram':
      return 'single_image';
    case 'facebook':
      return 'photo';
    case 'linkedin':
      return 'image';
    default:
      return 'single';
  }
}

/**
 * Reverse mapping: determine if a post type represents a series/multi-post
 * Used by platform adapters that need legacy boolean format
 *
 * @param platform_post_type - The platform post type
 * @returns true if the post type is a series/multi-post, false otherwise
 */
export function isSeriesFromPostType(platform_post_type: string): boolean {
  const multiPostTypes: PlatformPostType[] = [
    'thread',    // Twitter threads
    'carousel',  // Instagram/Facebook carousels
    'document'   // LinkedIn document carousels
  ];
  return multiPostTypes.includes(platform_post_type as PlatformPostType);
}

/**
 * Get the valid post types for a specific platform
 *
 * @param platform - The social media platform
 * @returns Array of valid post types for the platform
 */
export function getPostTypesForPlatform(platform: string): PlatformPostType[] {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return TWITTER_POST_TYPES;
    case 'instagram':
      return INSTAGRAM_POST_TYPES;
    case 'facebook':
      return FACEBOOK_POST_TYPES;
    case 'linkedin':
      return LINKEDIN_POST_TYPES;
    default:
      return TWITTER_POST_TYPES; // Default fallback
  }
}

/**
 * Validate if a post type is valid for a given platform
 *
 * @param platform_post_type - The post type to validate
 * @param platform - The platform to validate against
 * @returns true if valid, false otherwise
 */
export function isValidPostTypeForPlatform(
  platform_post_type: string,
  platform: string
): boolean {
  const validTypes = getPostTypesForPlatform(platform);
  return validTypes.includes(platform_post_type as PlatformPostType);
}

/**
 * Check if a post type supports a specific media type
 *
 * @param postType - The platform post type to check
 * @param mediaType - The media type to check for support
 * @returns true if the post type supports the media type, false otherwise
 */
export function supportsMediaType(postType: PlatformPostType, mediaType: MediaType): boolean {
  const metadata = POST_TYPE_LABELS[postType];
  if (!metadata) return false;
  return metadata.supportedMediaTypes.includes(mediaType);
}

/**
 * Get all supported media types for a post type
 *
 * @param postType - The platform post type
 * @returns Array of supported media types (e.g., ['image', 'video'])
 */
export function getSupportedMediaTypes(postType: PlatformPostType): MediaType[] {
  const metadata = POST_TYPE_LABELS[postType];
  return metadata?.supportedMediaTypes || [];
}
